import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ChatTimeline.css';

const ChatTimeline = ({ currentUser, contactId, timelineEntriesProps }) => {
    const [timelineEntries, setTimelineEntries] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
    const [previewURLs, setPreviewURLs] = useState([]);
    const [expandedMedia, setExpandedMedia] = useState(null);

    useEffect(() => {
        setTimelineEntries(timelineEntriesProps || []);
    }, [timelineEntriesProps]);

    const handleMediaChange = (e) => {
        const files = Array.from(e.target.files);
        setSelectedMediaFiles(files);
        setPreviewURLs(files.map(file => URL.createObjectURL(file)));
    };

    const handleAddNote = async () => {
        if (!newNote.trim() && selectedMediaFiles.length === 0) return;

        const formData = new FormData();
        formData.append("organization", currentUser.organization);
        formData.append("contactId", contactId);
        formData.append("note", newNote);
        formData.append("userId", currentUser.userId);
        formData.append("userName", currentUser.fullname);
        selectedMediaFiles.forEach((file) => formData.append("media", file));

        try {
            const response = await axios.post(
                "https://gambot.azurewebsites.net/api/Webhooks/AddTimelineEntry",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (response.data?.Success) {
                setTimelineEntries(prev =>
                    [response.data?.Data, ...prev].sort(
                        (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
                    )
                );
                setNewNote('');
                setSelectedMediaFiles([]);
                setPreviewURLs([]);
            } else {
                console.error("Error adding timeline note:", response.data?.message);
            }
        } catch (error) {
            console.error("Error adding timeline note:", error);
        }
    };

    const buildTimelineMessage = (item) => {
        switch (item?.TimelineType) {
            case 'assign': return `Assigned to: ${item?.assignToName || 'Unknown'}`;
            case 'open conversation': return `Opened conversation`;
            case 'note': return `Note added: ${item?.note || 'No details provided'}`;
            case 'status change': return `${item?.note || 'No details provided'}`;
            case 'close': return `Conversation closed`;
            case 'botomation send message': return `Botomation message sent: ${item?.note || 'No Template'}`;
            case 'external webhook request creates message': return `External webhook message: ${item?.note || ''}`;
            case 'start conversation manually': return `Manual conversation start: ${item?.note || ''}`;
            case 'campaign message sent': return `Campaign: ${item?.note} sent by: ${item?.sentByName || 'Unknown'}`;
            case 'category change': return `${item?.note || 'Category changed'} by ${item?.createdByName || 'Unknown'}`;
            default: return `Timeline event: ${item?.TimelineType || 'Unknown'}`;
        }
    };

    const handleMediaClick = (media) => {
        if (media.mimeType?.startsWith('image')) {
            setExpandedMedia(media);
        } else if (media.mimeType?.startsWith('application') || media.mimeType?.startsWith('text')) {
            window.open(media.mediaUrl, '_blank');
        }
    };

    return (
        <div className="timeline-container">
            <h3>Timeline</h3>
            <div className="add-timeline-note">
                <textarea
                    placeholder="Add a new note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                />
                <div className="ChatTimeline-media-upload">
                    <label className="ChatTimeline-media-label">
                        Add media:
                        <input
                            type="file"
                            accept="image/*,audio/*,application/*"
                            multiple
                            onChange={handleMediaChange}
                            className="ChatTimeline-file-input"
                        />
                    </label>
                    <div className="ChatTimeline-media-preview-multiple">
                        {previewURLs.map((url, index) => {
                            const file = selectedMediaFiles[index];
                            return (
                                <div key={index} className="ChatTimeline-media-preview">
                                    {file?.type?.startsWith("image") && (
                                        <img src={url} alt="preview" className="ChatTimeline-img-preview" />
                                    )}
                                    {file?.type?.startsWith("audio") && (
                                        <audio controls className="ChatTimeline-audio-preview">
                                            <source src={url} type={file?.type} />
                                        </audio>
                                    )}
                                    {file?.type?.startsWith("application") && (
                                        <a href={url} download={file.name} className="ChatTimeline-doc-link">
                                            📎 {file.name}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <button className="add-timeline-button" onClick={handleAddNote} disabled={!newNote.trim() && selectedMediaFiles.length === 0}>
                    Add note
                </button>
            </div>

            <div className="timeline-list">
                {isLoading ? (
                    <p>Loading...</p>
                ) : (
                    timelineEntries.map((entry, index) => {
                        const mediaList = entry.mediaFiles || entry.MediaFiles || [];
                        return (
                            <div key={index} className="timeline-entry">
                                <div className="timeline-header">
                                    <div className="timeline-icon"><i className="fas fa-sticky-note"></i></div>
                                    <div className="timeline-info">
                                        <p className="timeline-note">{buildTimelineMessage(entry)}</p>
                                        <p className="timeline-meta">
                                            By: {entry.createdByName || 'Unknown'} | On: {new Date(entry.createdOn).toLocaleString()}
                                        </p>
                                        {mediaList.map((media, i) => (
                                            <div
                                                key={i}
                                                className="ChatTimeline-media-preview"
                                                onClick={() => handleMediaClick(media)}
                                            >
                                                {media.mimeType?.startsWith('image') && (
                                                    <img src={media.mediaUrl} alt="preview" className="ChatTimeline-img-preview" />
                                                )}
                                                {media.mimeType?.startsWith('audio') && (
                                                    <audio controls className="ChatTimeline-audio-preview">
                                                        <source src={media.mediaUrl} type={media.mimeType} />
                                                    </audio>
                                                )}
                                                {media.mimeType?.startsWith('application') && (
                                                    <div className="ChatTimeline-doc-wrapper">
                                                        📄 <span className="ChatTimeline-doc-name">{media.fileName || 'File'}</span>
                                                        <a href={media.mediaUrl} target="_blank" rel="noopener noreferrer" className="ChatTimeline-doc-download">Open</a>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {expandedMedia && (
                <div className="ChatTimeline-media-modal" onClick={() => setExpandedMedia(null)}>
                    <img src={expandedMedia.mediaUrl} alt="Expanded preview" className="ChatTimeline-modal-img" />
                </div>
            )}
        </div>
    );
};

export default ChatTimeline;
